import { createContext, useEffect, useState } from "react";
import { Box, Button, Stack, useMediaQuery, useTheme } from "@mui/material";
import ListingTable from "./listing";
import BasicBreadcrumbs from "../../components/breadcrumb";
import PrivateLayout from "../../layouts/privateLayout";
import { useLocation } from "react-router-dom";
import { useApiClient } from "../../context/ApiClientContext";
import ErrorAlert from "../../helper/errorAlert";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../../helper/baseApi";
import ConfirmDialog from "../../helper/confirmDialog";
import ComponentBackdrop from "../../components/backdrop";

export const DataContext = createContext();

function Index() {
  
  const location = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showButton, setShowButton] = useState(false);
  const [filteredDataList, setFilteredDataList] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);

  const {apiUrls: { mobileApiUrl },} = useApiClient();
  const navigate = useNavigate();

  const handleOpenConfirm = () => {
    if (selectedIds.length === 0) {
      toast.warning("Please select at least one mission to delete.");
      return;
    }
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => setOpenConfirm(false);

  const handleShowButton = (ids) => {
    setSelectedIds(ids);
    setShowButton(ids.length > 0);
  };

  useEffect(() => {
    fetchData();
  }, [location.pathname]);

  const fetchData = async (page) => {
    try {

      const apiUrl = `${mobileApiUrl}mission/view-list`;
      const response = await api.get(apiUrl);

      const missionList = response?.data?.data?.missionList || [];
      const dataWithKeys = missionList.map((item) => ({
        ...item,
        id: item.missionId
      }));
      setFilteredDataList(dataWithKeys);
    } catch (error) {
        setFilteredDataList([]);
        ErrorAlert(error, navigate);
    }
  };

  const handleConfirmDelete = async () => {
    setOpenBackdrop(true);
    try {      
      const apiUrl = `${mobileApiUrl}mission/delete`;
      const response = await api.delete(apiUrl, {
         data: { missionId: selectedIds }, 
      });

      if (response.status === 200) {
        const deletedItems = filteredDataList.filter(item =>
          selectedIds.includes(item.id)
        );
        const deletedNames = deletedItems.map(item => item.name);

        toast.success(`Deleted: ${deletedNames.join(", ")}`);

        setFilteredDataList(prev =>
          prev.filter(item => !selectedIds.includes(item.id))
        );
        setSelectedIds([]);
      } else {
        toast.error("Failed to delete mission. Please try again.");
      }
    } catch (error) {
      console.error("Delete Error:", error);
      ErrorAlert(error, navigate);
    } finally {
      setOpenBackdrop(false);
      setOpenConfirm(false);
    }
  };

  return (
    <PrivateLayout>
      <Box sx={{ mx: "40px", mt: 5 }}>
        <ComponentBackdrop openBackdrop={openBackdrop} />
        
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <BasicBreadcrumbs first="Mission"/>  

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{
              width: { xs: "100%", sm: "auto" },
              alignItems: { xs: "stretch", sm: "flex-start" },
            }}
          >
          {showButton && (
            <Button
              variant="contained"
              size="large"
              fullWidth={isMobile}
              sx={{
                backgroundColor: "secondary.main",
                fontWeight: "600",
                "&:hover": { backgroundColor: "secondary.dark" },
                whiteSpace: "nowrap",
              }}
                onClick={handleOpenConfirm}
            >
              Delete
            </Button>
          )}
        </Stack>

        <ConfirmDialog
              open={openConfirm}
              onClose={handleCloseConfirm}
              onConfirm={handleConfirmDelete}
              title="Delete Mission"
              message={`Are you sure you want to delete ${selectedIds.length > 1 ? "these mission" : "this mission"
                }?`}
              items={filteredDataList
                .filter(item => selectedIds.includes(item.id))
                .map(item => item.title)}
              confirmText="Delete"
              confirmColor="error"
            />

        </Box>
          <ListingTable
            dataList = {filteredDataList}
            handleShowButton={handleShowButton}
          />
      </Box>
    </PrivateLayout>
  );
}

export default Index;
